import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const TR = {
  sk: { missing: "Chýba session_id v URL", failed: "Prihlásenie zlyhalo", back: "Späť na prihlásenie", logging: "Prihlasujem cez Google…", verifying: "Sekundu, overujem session" },
  en: { missing: "Missing session_id in URL", failed: "Sign-in failed", back: "Back to sign-in", logging: "Signing in via Google…", verifying: "One moment, verifying session" },
  cs: { missing: "Chybí session_id v URL", failed: "Přihlášení selhalo", back: "Zpět na přihlášení", logging: "Přihlašuji přes Google…", verifying: "Sekundu, ověřuji session" },
  de: { missing: "session_id fehlt in der URL", failed: "Anmeldung fehlgeschlagen", back: "Zurück zur Anmeldung", logging: "Anmeldung über Google…", verifying: "Einen Moment, Session wird überprüft" },
  pl: { missing: "Brakuje session_id w URL", failed: "Logowanie nie powiodło się", back: "Wróć do logowania", logging: "Logowanie przez Google…", verifying: "Chwila, weryfikuję sesję" },
  fr: {
  missing: "session_id manquant dans l'URL",
  failed: "Connexion échouée",
  back: "Retour à la connexion",
  logging: "Connexion via Google…",
  verifying: "Un instant, vérification de la session"
},
};

// Handles return from Emergent Google Auth.
// URL: /auth/callback#session_id=XXX (optionally ?next=/somewhere)
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setUserFromToken } = useAuth();
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const processed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    const params = new URLSearchParams(loc.search);
    const next = params.get("next") || "/";

    if (!sessionId) {
      setErr(tr.missing);
      return;
    }

    (async () => {
      try {
        const langPref = (typeof window !== "undefined" && localStorage.getItem("wb_lang")) || "sk";
        const r = await api.post("/auth/google/session", {}, {
          headers: { "X-Session-ID": sessionId, "X-Lang": langPref },
        });
        const { token, user, requires_billing } = r.data;
        setUserFromToken(token, user);
        // Clean URL fragment
        window.history.replaceState({}, "", "/");
        if (requires_billing) {
          nav("/profile/billing?first=1&next=" + encodeURIComponent(next), { replace: true });
        } else {
          nav(next, { replace: true });
        }
      } catch (e) {
        setErr(e.response?.data?.detail || tr.failed);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="auth-callback" className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {err ? (
          <>
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" strokeWidth={1.4} />
            <h1 className="font-display font-bold text-xl mb-2">{tr.failed}</h1>
            <p className="text-sm text-zinc-400">{err}</p>
            <button onClick={() => nav("/login")} className="wb-btn-primary mt-5 px-5 py-2 text-sm">{tr.back}</button>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-gold-500 mx-auto mb-3 animate-spin" strokeWidth={1.4} />
            <h1 className="font-display font-bold text-xl mb-2">{tr.logging}</h1>
            <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">{tr.verifying}</p>
          </>
        )}
      </div>
    </div>
  );
}
